import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  CalculateCostsResponseSchema,
  CostingMethodEnum,
  UpdateItemParams,
} from "@forgepoint/shared";
import { useItemsStore, useItemVersionMs } from "./itemsStore";
import ItemEditor from "./ItemEditor";
import BOMBuilder from "./BOMBuilder";
import { BOMMarginEditor } from "./BOMMarginEditor";
import { BOMYieldEditor } from "./BOMYieldEditor";
import { UnitPriceTable } from "./UnitPriceTable";
import { Lock, Loader2 } from "lucide-react";
import { fetchApi } from "@/lib/api";
import Decimal from "decimal.js";
import type { QuoteCurrencyFields } from "@/lib/quoteCurrency";

interface BOMWrapperProps {
  itemId: string;
  quantities: string[];
  onUpdated?: () => void;
  /** When provided, clicking a component navigates within the parent sheet instead of opening a new one. */
  onNavigateToItem?: (itemId: string) => void;
  /** Prevents editing integration-controlled operations, components, and yield. */
  locked?: boolean;
  /** The quote's currency fields; a multi-currency quote adds its currency price to the pricing table. */
  currencyContext?: QuoteCurrencyFields;
}

interface CostData {
  quantity: string;
  unitCost: string;
  unitPrice: string;
}

export default function BOMWrapper({
  itemId,
  quantities,
  onUpdated,
  onNavigateToItem,
  locked = false,
  currencyContext,
}: BOMWrapperProps) {
  const { items, itemLoadingStates, fetchItem } = useItemsStore();
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [costs, setCosts] = useState<CostData[]>([]);
  const [isLoadingCosts, setIsLoadingCosts] = useState(false);
  // A component item's own cost save bumps the child, not this item, so the
  // totals below refetch on this counter as well.
  const [bomRefreshKey, setBomRefreshKey] = useState(0);
  const itemVersionMs = useItemVersionMs(itemId);
  const scrollRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isSyncing = useRef(false);

  const item = items.get(itemId);
  const loadingState = itemLoadingStates.get(itemId);
  const isLoading = loadingState?.isLoadingGet || false;

  useEffect(() => {
    if (!item) {
      fetchItem(itemId);
    }
  }, [itemId, item, fetchItem]);

  useEffect(() => {
    // A superseded run must not publish: two version bumps can overlap and
    // land out of order.
    let cancelled = false;
    const fetchCosts = async () => {
      if (!itemId) {
        return;
      }

      setIsLoadingCosts(true);
      try {
        const response = await fetchApi(
          `items/${itemId}/calculate-costs`,
          "POST",
          { body: { quantities } },
        );
        const { costs } = CalculateCostsResponseSchema.parse(response.data);

        if (!cancelled && response.ok && response.data) {
          setCosts(costs);
        }
      } catch {
        // Failed to fetch costs
      } finally {
        setIsLoadingCosts(false);
      }
    };

    fetchCosts();
    return () => {
      cancelled = true;
    };
  }, [itemId, quantities, itemVersionMs, bomRefreshKey]);

  /** Refreshes this item's totals after any save inside the BOM table. */
  const handleBomUpdated = useCallback(() => {
    setBomRefreshKey((key) => key + 1);
    onUpdated?.();
  }, [onUpdated]);

  /** Creates a callback ref that registers a scroll container at the given index. */
  const createScrollRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      scrollRefs.current[index] = el;
    },
    [],
  );

  /** Syncs horizontal scroll position across all registered scroll containers. */
  const handleScrollSync = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (isSyncing.current) {
      return;
    }
    isSyncing.current = true;
    const { scrollLeft } = e.currentTarget;
    scrollRefs.current.forEach((container) => {
      if (container && container !== e.currentTarget) {
        container.scrollLeft = scrollLeft;
      }
    });
    requestAnimationFrame(() => {
      isSyncing.current = false;
    });
  }, []);

  const handleUpdateItem = async (
    updates: UpdateItemParams,
  ): Promise<true | string> => {
    const updatesOnlyMargin =
      updates.margin !== undefined &&
      Object.keys(updates).every((field) => field === "margin");
    if (locked && !updatesOnlyMargin) {
      return "Synced items are read-only";
    }
    const { updateItem } = useItemsStore.getState();
    const { item, error } = await updateItem(itemId, updates);
    if (item) {
      onUpdated?.();
      return true;
    }
    return error ?? "Failed to update item";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!item) {
    return <div className="p-4 text-muted-foreground">Item not found</div>;
  }

  const isPriceModelLocked = Boolean(item.priceModelId);
  const isBomLocked = locked || isPriceModelLocked;
  const lockReason = locked
    ? "Controlled by integration"
    : "Controlled by price model";

  return (
    <>
      <div className="space-y-4">
        {isBomLocked && (
          <div className="flex items-center gap-3 text-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-200 px-4 py-3 rounded-lg">
            <div className="flex items-center justify-center h-7 w-7 rounded-full bg-amber-100 dark:bg-amber-900/40 shrink-0">
              <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <span className="font-medium">{lockReason}</span>
              <span className="text-amber-700 dark:text-amber-300">
                {" "}
                — operations, components, and yield are read-only. Margins stay
                editable. Open a component to change the cost on its item.
              </span>
            </div>
          </div>
        )}
        <div
          ref={createScrollRef(0)}
          onScroll={handleScrollSync}
          className="bg-card rounded-lg border shadow-sm overflow-x-auto"
        >
          {/* Only show BOM table for MANUFACTURED items */}
          {item.costingMethod === CostingMethodEnum.BOM ? (
            <table
              className="w-full border-separate border-spacing-0 table-fixed"
              style={{ minWidth: `${300 + quantities.length * 120}px` }}
            >
              <thead className="bg-muted sticky top-0 z-10">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-sm w-[300px] min-w-[300px] sticky left-0 z-20 bg-muted border-r border-border border-b-2 border-b-border">
                    Name
                  </th>
                  {quantities.map((qty) => (
                    <th
                      key={qty}
                      className="text-right py-3 px-4 font-semibold text-sm min-w-[100px] border-l border-border border-b-2 border-b-border"
                    >
                      Qty {qty}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* BOM Builder for operations and components */}
                <BOMBuilder
                  item={item}
                  quantities={quantities}
                  onUpdated={handleBomUpdated}
                  onNavigateToItem={onNavigateToItem}
                  locked={isBomLocked}
                  lockReason={lockReason}
                  refreshKey={bomRefreshKey}
                />

                {/* Cost totals row */}
                <tr className="bg-accent font-semibold">
                  <td className="py-2 px-4 text-sm sticky left-0 z-10 bg-accent border-r border-border border-t-2 border-t-border">
                    Total Cost
                  </td>
                  {isLoadingCosts
                    ? quantities.map((_, i) => (
                        <td
                          key={i}
                          className="py-2 px-4 text-right border-l border-border border-t-2 border-t-border"
                        >
                          <Loader2 className="h-4 w-4 animate-spin inline" />
                        </td>
                      ))
                    : costs.length > 0
                      ? costs.map((cost, i) => {
                          const unitCostDecimal = new Decimal(cost.unitCost);
                          const totalCost = unitCostDecimal
                            .times(cost.quantity)
                            .toNumber();
                          return (
                            <td
                              key={i}
                              className="py-2 px-4 text-right text-sm border-l border-border border-t-2 border-t-border tabular-nums"
                            >
                              <div>
                                $
                                {unitCostDecimal
                                  .toNumber()
                                  .toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                /ea
                              </div>
                              <div className="text-[10px] text-muted-foreground font-normal">
                                $
                                {totalCost.toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 3,
                                })}
                              </div>
                            </td>
                          );
                        })
                      : quantities.map((_, i) => (
                          <td
                            key={i}
                            className="py-2 px-4 text-right border-l border-border border-t-2 border-t-border"
                          >
                            —
                          </td>
                        ))}
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              This item uses unit cost pricing. Switch to BOM costing method to
              manage operations and components.
            </div>
          )}
        </div>

        {/* Yield and Margins section - only for MANUFACTURED items */}
        {item.costingMethod === CostingMethodEnum.BOM && (
          <>
            <BOMYieldEditor
              itemId={itemId}
              yields={item.yield || []}
              quantities={quantities}
              onUpdate={handleUpdateItem}
              scrollRef={createScrollRef(1)}
              onScrollSync={handleScrollSync}
              locked={isBomLocked}
            />

            <BOMMarginEditor
              itemId={itemId}
              margins={item.margin || []}
              quantities={quantities}
              costingMethod={item.costingMethod}
              onUpdate={handleUpdateItem}
              scrollRef={createScrollRef(2)}
              onScrollSync={handleScrollSync}
            />

            <UnitPriceTable
              itemId={itemId}
              quantities={quantities}
              scrollRef={createScrollRef(3)}
              onScrollSync={handleScrollSync}
              refreshKey={bomRefreshKey}
              currencyContext={currencyContext}
            />
          </>
        )}
      </div>

      {/* Edit Item Sheet */}
      <ItemEditor
        open={isEditSheetOpen}
        onOpenChange={setIsEditSheetOpen}
        itemId={itemId}
        onSave={handleUpdateItem}
        mode="edit"
      />
    </>
  );
}
